import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

const STATUS_FLOW = ['pending', 'packed', 'dispatched', 'delivered'];
const STATUS_COLORS = { pending: 'badge-pending', packed: 'badge-packed', dispatched: 'badge-dispatched', delivered: 'badge-delivered', cancelled: 'badge-cancelled' };

const ApartmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingAll, setUpdatingAll] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assign, setAssign] = useState({ staffId: '', vehicleId: '' });

  const fetch = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/apartments/${id}`);
      setData(res.data);

      if (user?.role === 'ADM' || user?.role === 'MGR') {
        const [staffRes, vehicleRes] = await Promise.all([
          api.get('/staff'),
          api.get('/vehicles'),
        ]);
        setStaff(staffRes.data.data);
        setVehicles(vehicleRes.data.data);
      }
    } catch { toast('Failed to load apartment details', 'error'); }
    finally { setLoading(false); }
  }, [id, toast, user?.role]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast(`Order updated to ${status}`, 'success');
      fetch();
    } catch { toast('Failed to update status', 'error'); }
  };

  const bulkProcess = async (targetStatus) => {
    const pendingOrders = data.customers
      .flatMap(c => c.orders)
      .filter(o => o.order_status === 'pending')
      .map(o => o.id);
    if (pendingOrders.length === 0) { toast('No pending orders to process', 'info'); return; }
    if (!window.confirm(`Mark ${pendingOrders.length} orders as "${targetStatus}"?`)) return;
    setUpdatingAll(true);
    try {
      await api.put('/orders/bulk-status', { orderIds: pendingOrders, status: targetStatus });
      toast(`${pendingOrders.length} orders updated!`, 'success');
      fetch();
    } catch { toast('Bulk update failed', 'error'); }
    finally { setUpdatingAll(false); }
  };

  const bulkAssign = async () => {
    if (!assign.staffId) { toast('Select a staff member first', 'warning'); return; }
    const allOrders = data.customers.flatMap(c => c.orders).map(o => o.id);
    if (allOrders.length === 0) { toast('No orders to assign', 'info'); return; }
    
    setUpdatingAll(true);
    try {
      await api.post('/staff/assign-bulk', { 
        staffId: assign.staffId, 
        vehicleId: assign.vehicleId || null, 
        orderIds: allOrders 
      });
      toast(`Assigned ${allOrders.length} orders successfully!`, 'success');
      fetch();
    } catch { toast('Bulk assignment failed', 'error'); }
    finally { setUpdatingAll(false); }
  };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;
  if (!data) return <div className="empty-state"><div className="empty-icon">⚠️</div><p>Apartment not found</p></div>;

  const { apartment, customers, totalOrders } = data;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/apartments')}>← Back</button>
        <div style={{ flex: 1, minWidth: 300 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>🏢 {apartment.apartment_name}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>📍 {apartment.location || apartment.address || 'No location'} · {totalOrders} orders</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" disabled={updatingAll} onClick={() => bulkProcess('packed')}>
            {updatingAll ? '...' : '📦 Pack Pending'}
          </button>
          <button className="btn btn-primary" disabled={updatingAll} onClick={() => bulkProcess('dispatched')}>
            {updatingAll ? '...' : '🚚 Dispatch Pending'}
          </button>
        </div>
      </div>

      {/* Bulk Assign Delivery Card */}
      {user?.role !== 'STF' && totalOrders > 0 && (
        <div className="card" style={{ marginBottom: 20, padding: 16, background: 'var(--dark-2)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>👥 Assign Delivery to All Orders in Apartment</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label className="form-label" style={{ fontSize: 12 }}>Staff Member</label>
              <select className="form-select" style={{ padding: '8px 12px' }} value={assign.staffId} onChange={e => setAssign({ ...assign, staffId: e.target.value })}>
                <option value="">Select staff...</option>
                {staff.filter(s => s.role === 'STF').map(s => (
                  <option key={s.id} value={s.id}>{s.full_name || s.username}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label className="form-label" style={{ fontSize: 12 }}>Vehicle (Optional)</label>
              <select className="form-select" style={{ padding: '8px 12px' }} value={assign.vehicleId} onChange={e => setAssign({ ...assign, vehicleId: e.target.value })}>
                <option value="">Select vehicle...</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number} ({v.type})</option>)}
              </select>
            </div>
            <button className="btn btn-green" disabled={updatingAll} onClick={bulkAssign} style={{ minWidth: 120 }}>
              {updatingAll ? 'Assigning...' : '✓ Assign All'}
            </button>
          </div>
        </div>
      )}

      {/* Customers */}
      {customers.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">📭</div><p>No orders for this apartment yet</p></div>
      ) : (
        <div className="form-grid">
          {customers.map(customer => (
            <div key={customer.phone} className="card">
              {/* Customer header */}
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setExpandedCustomer(expandedCustomer === customer.phone ? null : customer.phone)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16 }}>
                    {customer.customerName[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{customer.customerName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>📞 {customer.phone} · {customer.orders.length} order{customer.orders.length > 1 ? 's' : ''}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>₹{customer.orders.reduce((s, o) => s + parseFloat(o.total_amount), 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total value</div>
                  </div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>{expandedCustomer === customer.phone ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Orders expanded */}
              {expandedCustomer === customer.phone && (
                <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  {customer.orders.map(order => (
                    <div key={order.id} style={{ background: 'var(--dark-3)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Order #{order.id.slice(0, 8).toUpperCase()}</div>
                          <div style={{ fontWeight: 600, marginTop: 2 }}>₹{parseFloat(order.total_amount).toLocaleString('en-IN')} · {order.payment_method.toUpperCase()}</div>
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/orders/${order.id}`)}>Details</button>
                      </div>
                      {/* Items */}
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                        {Array.isArray(order.items) ? order.items.map(item => (
                          <div key={item.productId}>{item.name} × {item.quantity} = ₹{item.subtotal?.toFixed(2)}</div>
                        )) : null}
                      </div>
                      {/* Status flow */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Status:</span>
                        {STATUS_FLOW.map(s => (
                          <button
                            key={s}
                            className={`status-pill ${order.order_status === s ? STATUS_COLORS[s] + ' badge' : 'btn btn-secondary btn-sm'}`}
                            style={{ fontSize: 11, padding: '4px 10px', cursor: order.order_status === s ? 'default' : 'pointer' }}
                            onClick={() => order.order_status !== s && updateStatus(order.id, s)}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApartmentDetail;
